<script lang="ts">
	import type { BlogArticle } from '$models/Blog/BlogArticle';
	import { getSiteContext } from '$lib/context/site';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { formatDate, sanitizeHtmlToPlainText } from '$lib/utils';

	let { article, highlight = false }: { article: BlogArticle; highlight?: boolean } = $props();

	const site = getSiteContext();
</script>

<article class="blog-card" class:blog-card--highlight={highlight}>
	<a
		href={article.button.url}
		class="blog-card-link"
		aria-label={sanitizeHtmlToPlainText(article.title)}
	></a>

	<figure class="blog-card-image">
		{#if article.featuredImage}
			<img
				src={article.featuredImage.url}
				srcset={getWordPressImageSrcSet(article.featuredImage)}
				sizes="(min-width: 768px) 33vw, 100vw"
				alt={article.featuredImage.alt}
				width={article.featuredImage.width}
				height={article.featuredImage.height}
				loading="lazy"
				decoding="async"
			/>
		{/if}
	</figure>

	<div class="blog-card-content">
		{#if highlight}
			<div class="blog-card-label">Uitgelicht</div>
		{:else}
			<time class="blog-card-label" datetime={article.date}>
				{formatDate(new Date(article.date), site.locale.code)}
			</time>
		{/if}

		<h3 class="blog-card-title h4">{@html article.title}</h3>
		<p class="blog-card-intro">{sanitizeHtmlToPlainText(article.intro)}</p>

		{#if highlight}
			<div class="button button--dark">{article.button.title}</div>
		{:else}
			<span class="blog-card-cta">{article.button.title}</span>
		{/if}
	</div>
</article>

<style lang="postcss">
	.blog-card {
		position: relative;
		display: flex;
		flex-direction: column;
		height: 100%;
		border-radius: 16px;
		overflow: hidden;

		&-link {
			position: absolute;
			inset: 0;
			z-index: 2;
			cursor: pointer;

			&:focus-visible {
				outline: 2px solid var(--color-brown);
				outline-offset: 2px;
			}
		}

		&-image {
			overflow: hidden;
			border-radius: 16px 16px 0 0;
			aspect-ratio: 5 / 3;
			background-color: var(--variant-tint-2-color);

			img {
				display: block;
				width: 100%;
				height: 100%;
				object-fit: cover;
				object-position: center center;
				transition: transform var(--transition);
			}
		}

		&-content {
			flex: 1;
			display: flex;
			flex-direction: column;
			padding: 40px 16px 24px;
			background-color: var(--color-white);
		}

		&-label {
			display: block;
			font-family: var(--font-secondary);
			font-size: 14px;
			text-transform: uppercase;
			line-height: 1;
			color: var(--color-brown);
			margin-bottom: 8px;
		}

		&-title {
			font-family: var(--font-secondary);
			text-transform: uppercase;
			margin-bottom: 16px;
		}

		&-intro {
			display: -webkit-box;
			-webkit-line-clamp: 3;
			line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
			font-size: 14px;
			margin-bottom: 32px;
		}

		&-cta {
			font-family: var(--font-secondary);
			margin-top: auto;
			text-transform: uppercase;
			font-size: 18px;
			color: var(--color-brown);
		}

		&--highlight &-intro {
			font-size: 16px;
			-webkit-line-clamp: 8;
			line-clamp: 8;
		}

		&--highlight &-image {
			:global(.type-author) & {
				border-radius: 16px;
			}
		}

		&--highlight:hover .button {
			color: var(--color-white);
			background-color: var(--color-brown-light);

			&:after,
			&:before {
				border-color: var(--color-white);
			}
		}

		@media (--md) {
			&:hover &-image img,
			&:focus-within &-image img {
				transform: scale(1.05);
			}

			&--highlight {
				background-color: var(--color-white);
				flex-direction: row;
				align-items: stretch;
				gap: 40px;
				min-height: 420px;
			}

			&--highlight &-image,
			&--highlight &-content {
				width: calc(50% - 20px);
			}

			&--highlight &-image {
				aspect-ratio: 3 / 2;
				border-radius: 16px 0 0 16px;
			}

			&--highlight &-content {
				padding: 40px 40px 40px 0;
				align-self: center;
				align-items: flex-start;
			}
		}

		@media (--lg) {
			&-intro {
				font-size: 16px;
			}

			&--highlight &-content {
				padding: 40px 64px 40px 0;
			}
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.blog-tile-image img {
			transition: none;
		}
	}
</style>
